import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  selectFilteredCategory,
  selectPropCategories,
  setPropCategoryFilter,
} from "../../app/slices/PropLibrarySlice";

const hexToRgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
};

/**
 * Category rail for the prop library. The tint comes from the category's
 * `hexColor`, which is API data, not a design token — so it stays literal.
 * Pointer-driven like the picker's rail; arrows belong to the results.
 */
export default function PropLibraryRail() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const categories = useSelector(selectPropCategories);
  const selectedCategory = useSelector(selectFilteredCategory);

  const entry = (options: {
    key: string;
    label: string;
    isSelected: boolean;
    hexColor?: string;
    onClick: () => void;
  }) => {
    const rgb = options.hexColor ? hexToRgb(options.hexColor) : null;
    const tint = rgb ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.1)` : undefined;

    return (
      <button
        key={options.key}
        type="button"
        role="tab"
        aria-selected={options.isSelected}
        onClick={options.onClick}
        style={{
          backgroundColor: tint,
          color: options.hexColor,
          borderColor: options.isSelected
            ? options.hexColor ?? "var(--pi-key-blue)"
            : "transparent",
        }}
        className={classNames(
          "flex items-center gap-[8px] text-left px-[10px] py-[7px] rounded-[6px] text-[13px] border transition-colors",
          !options.hexColor && [
            options.isSelected
              ? "bg-creator-primary-washed text-creator-text font-medium"
              : "text-creator-text-sub hover:bg-creator-childActive",
          ]
        )}
      >
        <span
          aria-hidden
          className="w-[8px] h-[8px] rounded-full shrink-0"
          style={{
            backgroundColor: options.hexColor ?? "var(--pi-menu-text-color-1)",
          }}
        />
        <span className="truncate">
          {options.label.charAt(0).toUpperCase() + options.label.slice(1)}
        </span>
      </button>
    );
  };

  return (
    <div
      role="tablist"
      aria-orientation="vertical"
      aria-label={t("prop_library_ui.categories_label") ?? ""}
      className="w-[168px] shrink-0 border-r border-creator-border bg-creator-primary-background p-[8px] flex flex-col gap-[6px] overflow-y-auto pit-quiet-scrollbar"
    >
      <div className="px-[10px] pt-[6px] pb-[4px] text-[10px] font-semibold uppercase tracking-[0.08em] text-creator-text-sub">
        {t("prop_library_ui.categories_label")}
      </div>
      {entry({
        key: "all",
        label: t("prop_library_ui.all"),
        isSelected: selectedCategory === "",
        onClick: () => dispatch(setPropCategoryFilter("")),
      })}
      {categories.map((category) =>
        entry({
          key: category.id,
          label: category.name,
          isSelected: category.name === selectedCategory,
          hexColor: category.hexColor,
          onClick: () => dispatch(setPropCategoryFilter(category.name)),
        })
      )}
    </div>
  );
}
